import React, { createContext, useContext, useEffect, useState } from 'react';

// Define theme types
type ThemeType = 'light' | 'dark' | 'system';

type ThemeContextType = {
  isDarkMode: boolean;
  isSystemTheme: boolean;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType>({
  isDarkMode: false,
  isSystemTheme: true,
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Check if system preference is dark mode
  const systemPrefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  // Initialize from localStorage or default to system theme
  const [isSystemTheme, setIsSystemTheme] = useState<boolean>(() => {
    const savedValue = localStorage.getItem('isSystemTheme');
    return savedValue === null ? true : savedValue === 'true';
  });
  
  const [systemDarkMode, setSystemDarkMode] = useState<boolean>(systemPrefersDark());
  
  const [userTheme, setUserTheme] = useState<'light' | 'dark'>(() => {
    const savedTheme = localStorage.getItem('userTheme') as 'light' | 'dark';
    return savedTheme || 'light';
  });
  
  // Determine if in dark mode based on system preference and user selection
  const isDarkMode = isSystemTheme ? systemDarkMode : userTheme === 'dark';

  // Listen for system preference changes when using system theme
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    // Update the class on the document element
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);
  
  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('isSystemTheme', String(isSystemTheme));
    localStorage.setItem('userTheme', userTheme);
  }, [isSystemTheme, userTheme]);

  // Toggle between system and user preference
  const toggleTheme = () => {
    if (isSystemTheme) {
      // If currently using system theme, switch to explicit user choice
      // (opposite of current system appearance)
      setIsSystemTheme(false);
      setUserTheme(systemDarkMode ? 'light' : 'dark');
    } else {
      // If already using explicit user choice, toggle between light/dark
      setUserTheme(userTheme === 'light' ? 'dark' : 'light');
    }
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, isSystemTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
